import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, X, Check, Ban, ArrowUpCircle, Loader2, Inbox,
  AlertTriangle, Paperclip, Package, PencilRuler, ChevronDown, ChevronRight, Info,
} from 'lucide-react';
import { api } from '@/api/client';

// ── small shared bits ─────────────────────────────────────────────────
function fmt(dt?: string | null) {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return dt as string; }
}
const STATUS_CHIP: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  'In-Progress': 'bg-blue-100 text-blue-700 border-blue-200',
  Completed: 'bg-teal-100 text-teal-700 border-teal-200',
};
const PRIO_CHIP: Record<string, string> = {
  High: 'bg-rose-50 text-rose-600 border-rose-200',
  Medium: 'bg-amber-50 text-amber-600 border-amber-200',
  Low: 'bg-slate-50 text-slate-500 border-slate-200',
};
function TypeBadge({ t }: { t?: string }) {
  const asset = (t ?? 'Asset') === 'Asset';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${asset ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200'}`}>
      {asset ? <Package size={11} /> : <PencilRuler size={11} />}{t ?? 'Asset'}
    </span>
  );
}

export function MyRequirementsPage() {
  const [tab, setTab] = useState<'mine' | 'approvals'>('mine');
  const { data: link } = useQuery({
    queryKey: ['me', 'req-link'],
    queryFn: async () => (await api.get('/me/requirements/link')).data as { linked: boolean; hasManager?: boolean; reportingManagerName?: string; employeeName?: string },
    staleTime: 5 * 60 * 1000, retry: false,
  });
  const { data: approvals } = useQuery({
    queryKey: ['me', 'approvals'],
    queryFn: async () => (await api.get('/me/approvals')).data as any[],
    staleTime: 30 * 1000,
  });
  const approvalCount = approvals?.length ?? 0;

  return (
    <div className="p-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="text-primary" size={22} />
        <h1 className="text-xl font-bold">My Requests</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Raise Asset &amp; Stationery requests and track their approval. Requests go to your Team Manager first.</p>

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <button onClick={() => setTab('mine')} className={`px-4 py-2 text-sm font-medium rounded-t ${tab === 'mine' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'}`}>My Requests</button>
        <button onClick={() => setTab('approvals')} className={`px-4 py-2 text-sm font-medium rounded-t flex items-center gap-1.5 ${tab === 'approvals' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
          For My Approval
          {approvalCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === 'approvals' ? 'bg-white text-primary' : 'bg-rose-500 text-white'}`}>{approvalCount}</span>}
        </button>
      </div>

      {tab === 'mine' ? <MyRequestsTab link={link} /> : <ApprovalsTab rows={approvals} />}
    </div>
  );
}

// ── My own requests ───────────────────────────────────────────────────
function MyRequestsTab({ link }: { link?: { linked: boolean; hasManager?: boolean; reportingManagerName?: string } }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'requirements'],
    queryFn: async () => (await api.get('/me/requirements')).data as any[],
  });
  const cancel = useMutation({
    mutationFn: async (id: number) => (await api.post(`/me/requirements/${id}/cancel`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me', 'requirements'] }); toast.success('Request cancelled'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not cancel'),
  });
  const linked = link?.linked !== false;

  return (
    <div>
      {!linked && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex gap-2">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>Your login isn&apos;t linked to an employee record yet, so you can&apos;t raise a request. Please ask HR to add your mobile number to your employee profile.</div>
        </div>
      )}
      {linked && link?.hasManager === false && (
        <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-sm flex gap-2">
          <Info size={18} className="shrink-0 mt-0.5" />
          <div>No Team Manager is set on your profile yet — your request will go to HR to assign an approver. (HR can set your Team Manager under HR → Employees.)</div>
        </div>
      )}
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-500">{data?.length ?? 0} request{(data?.length ?? 0) === 1 ? '' : 's'}</div>
        <button disabled={!linked} onClick={() => setShowForm(true)} className="bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"><Plus size={15} /> New Request</button>
      </div>

      {isLoading ? <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={18} />Loading…</div>
        : (data?.length ?? 0) === 0 ? <EmptyState icon={<Inbox size={30} />} text="No requests yet. Click “New Request” to raise one." />
        : <div className="space-y-2">{data!.map((r) => <RequestCard key={r.id} r={r} onCancel={() => cancel.mutate(r.id)} canceling={cancel.isPending} />)}</div>}

      {showForm && <NewRequestModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ['me', 'requirements'] }); }} />}
    </div>
  );
}

function RequestCard({ r, onCancel, canceling }: { r: any; onCancel: () => void; canceling: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <TypeBadge t={r.requestType} />
            <span className="font-semibold text-slate-800">{r.requirement}</span>
            <span className="text-xs text-slate-500">× {r.quantity}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIO_CHIP[r.priority] ?? ''}`}>{r.priority}</span>
          </div>
          {r.requirementDetails && <div className="text-sm text-slate-500 mt-1">{r.requirementDetails}</div>}
          <div className="text-xs text-slate-400 mt-1">Raised {fmt(r.createdAt)}
            {r.status === 'Pending' && r.currentApproverName && <> · Awaiting <b className="text-slate-600">{r.currentApproverName}</b>{r.currentLevel > 1 && ' (escalated)'}</>}
            {r.status === 'Pending' && !r.currentApproverName && <> · Awaiting HR to assign an approver</>}
          </div>
          {r.status === 'Rejected' && r.rejectionReason && <div className="text-xs text-rose-600 mt-1">✕ {r.rejectionReason}</div>}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_CHIP[r.status] ?? ''}`}>{r.status}</span>
          {r.status === 'Pending' && <button disabled={canceling} onClick={onCancel} className="text-xs text-rose-600 hover:underline disabled:opacity-40">Cancel</button>}
        </div>
      </div>
      {r.history?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <button onClick={() => setOpen(o => !o)} className="text-xs text-slate-500 flex items-center gap-1">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} History ({r.history.length})</button>
          {open && <Timeline history={r.history} />}
        </div>
      )}
    </div>
  );
}

function Timeline({ history }: { history: any[] }) {
  return (
    <ol className="mt-2 space-y-1.5 pl-1">
      {history.map((h) => (
        <li key={h.id} className="text-xs flex gap-2">
          <span className="text-slate-400 whitespace-nowrap">{fmt(h.createdAt)}</span>
          <span className="font-medium text-slate-700">{h.action}</span>
          {h.actorName && <span className="text-slate-500">by {h.actorName}</span>}
          {h.remarks && <span className="text-slate-500">— {h.remarks}</span>}
        </li>
      ))}
    </ol>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className="py-16 text-center text-slate-400"><div className="flex justify-center mb-2 text-slate-300">{icon}</div>{text}</div>;
}

function NewRequestModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [d, setD] = useState<any>({ requestType: 'Asset', requirement: '', quantity: 1, priority: 'Medium', requirementDetails: '' });
  const [attach, setAttach] = useState<{ name: string; url: string } | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      if (!d.requirement.trim()) throw new Error(`Please describe the ${d.requestType.toLowerCase()} you need`);
      const body = { ...d, quantity: Number(d.quantity) || 1, attachmentUrl: attach?.url };
      return (await api.post('/me/requirements', body)).data;
    },
    onSuccess: () => { toast.success('Request submitted'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Could not submit'),
  });
  const onFile = (f?: File) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('File too large (max 5 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setAttach({ name: f.name, url: String(reader.result) });
    reader.readAsDataURL(f);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[520px] p-5" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold">New Request</h2><button onClick={onClose}><X size={18} /></button></div>

        <label className="block text-[11px] text-slate-500 mb-1">What do you need?</label>
        <div className="flex gap-2 mb-3">
          {['Asset', 'Stationery'].map(t => (
            <button key={t} onClick={() => setD({ ...d, requestType: t })} className={`flex-1 py-2 text-sm rounded border flex items-center justify-center gap-1.5 ${d.requestType === t ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-300 text-slate-600'}`}>
              {t === 'Asset' ? <Package size={15} /> : <PencilRuler size={15} />}{t}
            </button>
          ))}
        </div>

        <label className="block text-[11px] text-slate-500 mb-0.5">Item / What you need <span className="text-rose-500">*</span></label>
        <input value={d.requirement} onChange={e => setD({ ...d, requirement: e.target.value })} placeholder={d.requestType === 'Asset' ? 'e.g. Laptop, SIM card, Chair' : 'e.g. A4 paper, Pens, Notebook'} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm mb-3" autoFocus />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] text-slate-500 mb-0.5">Quantity</label>
            <input type="number" min={1} value={d.quantity} onChange={e => setD({ ...d, quantity: e.target.value.replace(/[^0-9]/g, '') })} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-0.5">Priority</label>
            <select value={d.priority} onChange={e => setD({ ...d, priority: e.target.value })} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
              {['High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <label className="block text-[11px] text-slate-500 mb-0.5">Reason / details</label>
        <textarea value={d.requirementDetails} onChange={e => setD({ ...d, requirementDetails: e.target.value })} rows={3} placeholder="Why do you need this?" className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm mb-3" />

        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer mb-4">
          <Paperclip size={14} /><span className="underline">{attach ? attach.name : 'Attach a file (optional)'}</span>
          <input type="file" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
        </label>
        {attach && <button onClick={() => setAttach(null)} className="text-xs text-rose-500 ml-2 -mt-3 mb-3 block">remove attachment</button>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-600">Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-1.5 text-sm rounded bg-primary text-white flex items-center gap-1 disabled:opacity-50">
            {save.isPending && <Loader2 className="animate-spin" size={14} />}Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approvals inbox ───────────────────────────────────────────────────
function ApprovalsTab({ rows }: { rows?: any[] }) {
  const qc = useQueryClient();
  const [action, setAction] = useState<{ kind: 'approve' | 'reject' | 'escalate'; row: any } | null>(null);
  const act = useMutation({
    mutationFn: async (v: { kind: string; id: number; body: any }) => (await api.post(`/me/approvals/${v.id}/${v.kind}`, v.body)).data,
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['me', 'approvals'] });
      qc.invalidateQueries({ queryKey: ['me', 'requirements'] });
      setAction(null);
      toast.success(v.kind === 'approve' ? 'Approved' : v.kind === 'reject' ? 'Rejected' : 'Escalated to the next approver');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed'),
  });

  if ((rows?.length ?? 0) === 0) return <EmptyState icon={<Check size={30} />} text="Nothing is waiting for your approval right now." />;

  return (
    <div className="space-y-2">
      {rows!.map((r) => (
        <div key={r.id} className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge t={r.requestType} />
                <span className="font-semibold text-slate-800">{r.requirement}</span>
                <span className="text-xs text-slate-500">× {r.quantity}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIO_CHIP[r.priority] ?? ''}`}>{r.priority}</span>
              </div>
              <div className="text-sm text-slate-600 mt-1">From <b>{r.employeeName ?? r.employee?.employeeName ?? '—'}</b>{r.empCode ? ` (${r.empCode})` : ''}{r.department ? ` · ${r.department}` : ''}</div>
              {r.requirementDetails && <div className="text-sm text-slate-500 mt-0.5">{r.requirementDetails}</div>}
              <div className="text-xs text-slate-400 mt-1">Raised {fmt(r.createdAt)}{r.currentLevel > 1 && <> · you are approver at level {r.currentLevel}</>}</div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button onClick={() => setAction({ kind: 'approve', row: r })} className="text-xs px-2.5 py-1 rounded bg-emerald-600 text-white flex items-center gap-1"><Check size={13} /> Approve</button>
              <button onClick={() => setAction({ kind: 'reject', row: r })} className="text-xs px-2.5 py-1 rounded bg-rose-600 text-white flex items-center gap-1"><Ban size={13} /> Reject</button>
              <button onClick={() => setAction({ kind: 'escalate', row: r })} className="text-xs px-2.5 py-1 rounded border border-indigo-300 text-indigo-700 flex items-center gap-1"><ArrowUpCircle size={13} /> Escalate</button>
            </div>
          </div>
          {r.history?.length > 0 && <ApprovalHistory history={r.history} />}
        </div>
      ))}
      {action && <ApprovalActionModal a={action} busy={act.isPending} onClose={() => setAction(null)} onSubmit={(body) => act.mutate({ kind: action.kind, id: action.row.id, body })} />}
    </div>
  );
}

function ApprovalHistory({ history }: { history: any[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 pt-2 border-t border-slate-100">
      <button onClick={() => setOpen(o => !o)} className="text-xs text-slate-500 flex items-center gap-1">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} History ({history.length})</button>
      {open && <Timeline history={history} />}
    </div>
  );
}

function ApprovalActionModal({ a, onSubmit, onClose, busy }: { a: { kind: 'approve' | 'reject' | 'escalate'; row: any }; onSubmit: (b: any) => void; onClose: () => void; busy: boolean }) {
  const [text, setText] = useState('');
  const isReject = a.kind === 'reject';
  const title = a.kind === 'approve' ? 'Approve Request' : a.kind === 'reject' ? 'Reject Request' : 'Escalate to Higher Management';
  const desc = a.kind === 'approve' ? 'This will finally approve the request.'
    : a.kind === 'reject' ? 'Please give a reason so the employee understands.'
    : 'This passes the request to the next approver in the chain (Team Manager decides). If no higher level is set up, you\'ll be told to Approve or Reject instead.';
  const color = a.kind === 'approve' ? 'bg-emerald-600' : a.kind === 'reject' ? 'bg-rose-600' : 'bg-indigo-600';
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-24" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[440px] p-5" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-2"><h2 className="text-lg font-bold">{title}</h2><button onClick={onClose}><X size={18} /></button></div>
        <p className="text-sm text-slate-500 mb-3">{desc}</p>
        <div className="text-sm bg-slate-50 rounded p-2 mb-3"><TypeBadge t={a.row.requestType} /> <b className="ml-1">{a.row.requirement}</b> × {a.row.quantity} — {a.row.employeeName ?? a.row.employee?.employeeName}</div>
        <label className="block text-[11px] text-slate-500 mb-0.5">{isReject ? 'Reason *' : 'Remarks (optional)'}</label>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm mb-4" autoFocus />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-600">Cancel</button>
          <button disabled={busy || (isReject && !text.trim())} onClick={() => onSubmit(isReject ? { reason: text } : { remarks: text })} className={`px-4 py-1.5 text-sm rounded text-white flex items-center gap-1 disabled:opacity-50 ${color}`}>
            {busy && <Loader2 className="animate-spin" size={14} />}{a.kind === 'approve' ? 'Approve' : a.kind === 'reject' ? 'Reject' : 'Escalate'}
          </button>
        </div>
      </div>
    </div>
  );
}
